/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useState } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from '@/components/link';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import { routes } from '@/route-paths';
import { isEmailIdentifier } from '@/extensions/priceshoes/lib/auth/identifier';
import { type PasswordError, validatePassword } from '@/extensions/priceshoes/lib/auth/password';

interface PasswordFormProps {
    /** Partner ID or email already identified as VALID by Auth Extension (PRIC-165). */
    identifier: string;
    /** Error resolved by the login action. */
    error?: string;
    returnUrl?: string | null;
}

/**
 * Password screen for a registered (VALID) shopper (PRIC-171): shows the identifier entered on the
 * previous step, the password field and a way back to sign in with another account.
 */
export default function PasswordForm({ identifier, error, returnUrl }: PasswordFormProps): ReactElement {
    const { t } = useTranslation('extPriceshoes');
    const [passwordError, setPasswordError] = useState<PasswordError | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    // Validate before submitting so an empty password doesn't need a round trip; the action validates again.
    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        const password = new FormData(event.currentTarget).get('password')?.toString() ?? '';
        const validationError = validatePassword(password);
        setPasswordError(validationError);
        if (validationError) {
            event.preventDefault();
        }
    };

    const fieldError = passwordError ? t(`login.password.errors.${passwordError}`) : error;
    const identifierLabel = isEmailIdentifier(identifier)
        ? t('login.password.emailLabel')
        : t('login.password.partnerIdLabel');

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold text-foreground">{t('login.password.title')}</h1>
                <p className="text-foreground">
                    {identifierLabel}: {identifier}
                </p>
            </div>

            <Form method="post" className="space-y-6" noValidate onSubmit={handleSubmit}>
                {/* Lets password managers pair the password with the identifier entered on the previous step. */}
                <input type="text" name="identifier" value={identifier} autoComplete="username" readOnly hidden />

                <div className="space-y-2">
                    <Label htmlFor="password">{t('login.password.label')}</Label>
                    <div className="relative">
                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            className="pr-10"
                            aria-invalid={fieldError ? true : undefined}
                            aria-describedby={fieldError ? 'password-error' : undefined}
                            onChange={() => setPasswordError(null)}
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground cursor-pointer"
                            aria-label={
                                showPassword ? t('login.password.hidePassword') : t('login.password.showPassword')
                            }
                            aria-pressed={showPassword}
                            onClick={() => setShowPassword((visible) => !visible)}>
                            {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                        </button>
                    </div>
                    {fieldError ? (
                        <p id="password-error" role="alert" className="text-sm text-destructive">
                            {fieldError}
                        </p>
                    ) : null}
                    <div className="text-right">
                        <Link to={routes.forgotPassword} className="text-sm text-foreground underline">
                            {t('login.password.forgotPassword')}
                        </Link>
                    </div>
                </div>

                <input type="hidden" name="loginMode" value="priceshoesPassword" />
                {returnUrl ? <input type="hidden" name="returnUrl" value={returnUrl} /> : null}

                <FormSubmitButton defaultText={t('login.next')} submittingText={t('login.submitting')} />
            </Form>

            <div className="space-y-4 border-t border-border pt-6">
                <p className="text-center text-foreground">{t('login.password.notYou')}</p>
                {/* A plain navigation to /login drops the action data and goes back to the identification step. */}
                <Button asChild variant="outline" className="w-full">
                    <Link to={routes.login}>{t('login.password.otherAccount')}</Link>
                </Button>
            </div>
        </div>
    );
}
